import React, { useRef, useState } from "react";

import FilterListIcon from "@mui/icons-material/FilterList";
import UploadIcon from "@mui/icons-material/Upload";
import { InputAdornment } from "@mui/material";
import { Stack } from "@mui/system";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useSearchParams } from "react-router-dom-v5-compat";

import searchIcon from "@src/assets/svg/search_icon.svg";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import MenuOptionsWithCheckIfSelected from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import Popover from "@src/sharedComponents/Popover/Popover";
import TextInput from "@src/sharedComponents/TextInput/TextInput";

import styles from "./PortfolioPlan.module.css";

const hideFilters = true;
const popoverSpace = -10;

const filterModelsOptions = [
  {
    id: "activeModels",
    label: "Active Models",
  },

  {
    id: "inactiveModels",
    label: "Inactive Models",
  },
];

export type ModalType = "create" | "upload";

export const TableHeader = ({
  onInputChange,
  onCreateModelPress,
  onFilterChange,
  input,
}: {
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterChange: (filter: { id: string; label: string }) => void;
  onCreateModelPress: (modalType: ModalType) => void;
  input: string;
}): JSX.Element => {
  const [showFilterPopover, setShowFilterPopover] = useState(false);

  const { enablePortfolioLabPhase2 } = useFlags();

  const filterPopoverAnchorRef = useRef<HTMLDivElement | null>(null);

  const [searchParams] = useSearchParams();

  return (
    <Stack
      direction="row"
      justifyContent={"space-between"}
      paddingBottom={"20px"}
    >
      <Stack direction="row">
        <TextInput
          value={input ?? ""}
          onChange={onInputChange}
          placeholder={"Search models..."}
          style={{ width: "280px", marginRight: "20px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                <img src={searchIcon} />
              </InputAdornment>
            ),
          }}
        />

        {hideFilters ? null : (
          <Popover
            open={showFilterPopover}
            onClose={() => setShowFilterPopover(false)}
            anchor={filterPopoverAnchorRef.current}
            transformOriginVertical={popoverSpace}
            popoverElement={
              <div ref={filterPopoverAnchorRef}>
                <Button
                  text="Filters"
                  onClick={() => setShowFilterPopover(!showFilterPopover)}
                  variant="outlined"
                  buttonType="primary"
                  startIcon={<FilterListIcon />}
                  sx={{ height: "32px" }}
                />
              </div>
            }
          >
            <div className={styles.dropdowContainer}>
              <MenuOptionsWithCheckIfSelected
                curItemName={searchParams.get("filterBy") ?? undefined}
                onSelect={onFilterChange}
                options={filterModelsOptions}
              />
            </div>
          </Popover>
        )}
      </Stack>

      <Stack direction={"row"} spacing={2}>
        <Button
          text="Upload"
          onClick={() => onCreateModelPress("upload")}
          variant="outlined"
          buttonType="primary"
          endIcon={<UploadIcon sx={{ width: 18, height: 18 }} />}
          style={{ whiteSpace: "nowrap" }}
          sx={{ height: "32px" }}
        />

        {enablePortfolioLabPhase2 ? (
          <Button
            text="Create Model"
            onClick={() => onCreateModelPress("create")}
            variant="outlined"
            buttonType="secondary"
            style={{ whiteSpace: "nowrap" }}
            sx={{ height: "32px" }}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};
